import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, MessageCircle } from "lucide-react";
import { CORP_IMG_1, CORP_IMG_2 } from "@/data/content";
import { easeOutExpo, fadeUp } from "@/lib/motion";
import { API, waLink } from "@/lib/site";

const PERKS = [
  "Your logo printed on boxes & sleeves",
  "Personalized note cards in every hamper",
  "Bulk pricing from 10 boxes upward",
  "Pan-India delivery, branch-wise dispatch",
];

const QTY = ["10 – 25", "25 – 50", "50 – 100", "100+"];

export default function Corporate() {
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [qty, setQty] = useState(QTY[0]);
  const [occasion, setOccasion] = useState("Diwali");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!company.trim() || !phone.trim()) {
      toast.error("Please add your company name and phone number.");
      return;
    }
    setBusy(true);
    try {
      await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: company,
          phone,
          company,
          quantity: qty,
          occasion: `Corporate · ${occasion}`,
          kind: "corporate",
          message: `Bulk gifting request: ${qty} boxes for ${occasion}`,
        }),
      });
      toast.success("Request received! Our B2B specialist will call you within 24 hours.");
      setCompany("");
      setPhone("");
    } catch {
      toast.error("Could not reach the studio — ping us on WhatsApp instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="corporate" className="py-16 md:py-24 lg:py-32 bg-olive text-ivory scroll-mt-16 relative overflow-hidden" data-testid="corporate-section">
      <div className="pointer-events-none absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-gold/10 blur-[140px]" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <motion.div {...fadeUp}>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold-light" data-testid="corporate-eyebrow">
                Corporate & Bulk Gifting
              </p>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight" data-testid="corporate-heading">
                Gifts that carry <span className="italic text-gold-light">your brand</span>
              </h2>
              <p className="mt-5 max-w-lg text-sm sm:text-base leading-relaxed text-ivory/70" data-testid="corporate-sub">
                Diwali hampers, onboarding kits, client thank-yous — we design,
                brand and deliver at scale, with the same hand-packed care as a
                single gift.
              </p>
            </motion.div>

            <ul className="mt-8 space-y-4" data-testid="corporate-perks">
              {PERKS.map((p, i) => (
                <motion.li
                  key={p}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: 0.08 * i }}
                  className="flex items-start gap-3 text-sm text-ivory/85"
                  data-testid={`corporate-perk-${i}`}
                >
                  <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center">
                    <Check className="h-3 w-3 text-gold-light" />
                  </span>
                  {p}
                </motion.li>
              ))}
            </ul>

            <motion.div {...fadeUp} className="mt-10 grid grid-cols-2 gap-4 max-w-md">
              <div className="rounded-2xl overflow-hidden border border-ivory/15 rotate-[-2deg]">
                <img src={CORP_IMG_1} alt="Executive gift hamper with custom ribbon" className="img-warm w-full h-40 sm:h-48 object-cover" loading="lazy" data-testid="corporate-image-1" />
              </div>
              <div className="rounded-2xl overflow-hidden border border-ivory/15 rotate-[2deg] mt-6">
                <img src={CORP_IMG_2} alt="Corporate gifting exchange in a modern office" className="img-warm w-full h-40 sm:h-48 object-cover" loading="lazy" data-testid="corporate-image-2" />
              </div>
            </motion.div>
          </div>

          <motion.div
            {...fadeUp}
            className="rounded-3xl bg-olive-light/60 backdrop-blur border border-gold/25 p-6 sm:p-10"
            data-testid="corporate-quote-card"
          >
            <h3 className="font-display text-3xl font-semibold" data-testid="corporate-form-heading">Get a bulk quote</h3>
            <p className="mt-2 text-sm text-ivory/60">Two fields, one callback. We handle the rest.</p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light">Company / Name</label>
                <input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Acme Pvt Ltd"
                  className="mt-1.5 w-full rounded-xl bg-noir/40 border border-ivory/20 px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/35 outline-none focus:border-gold transition-colors duration-300"
                  data-testid="corporate-company-input"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light">Phone</label>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 …"
                  className="mt-1.5 w-full rounded-xl bg-noir/40 border border-ivory/20 px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/35 outline-none focus:border-gold transition-colors duration-300"
                  data-testid="corporate-phone-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light">Boxes</label>
                  <select
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    className="mt-1.5 w-full rounded-xl bg-noir/40 border border-ivory/20 px-4 py-3.5 text-sm text-ivory outline-none focus:border-gold transition-colors duration-300"
                    data-testid="corporate-quantity-select"
                  >
                    {QTY.map((q) => (
                      <option key={q} value={q} className="text-charcoal">{q}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light">Occasion</label>
                  <select
                    value={occasion}
                    onChange={(e) => setOccasion(e.target.value)}
                    className="mt-1.5 w-full rounded-xl bg-noir/40 border border-ivory/20 px-4 py-3.5 text-sm text-ivory outline-none focus:border-gold transition-colors duration-300"
                    data-testid="corporate-occasion-select"
                  >
                    {["Diwali", "New Year", "Onboarding", "Client Gifting", "Other"].map((o) => (
                      <option key={o} value={o} className="text-charcoal">{o}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-full bg-gold text-noir font-semibold text-sm px-6 py-4 hover:bg-gold-light transition-colors duration-300 disabled:opacity-60"
                data-testid="corporate-quote-submit-button"
              >
                {busy ? "Sending…" : "Request bulk quote"}
              </button>
              <a
                href={waLink("Hello A to Z Gift & Craft House! I want to discuss a corporate gifting order.")}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 text-gold-light font-semibold text-sm px-6 py-4 hover:bg-gold/10 transition-colors duration-300"
                data-testid="corporate-whatsapp-button"
              >
                <MessageCircle className="h-4 w-4" />
                Talk to a B2B specialist
              </a>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
